import {
  takeEvery,
  takeLatest,
  put,
  select,
  call,
  all,
} from 'redux-saga/effects';
import uniqid from 'uniqid';

import { getTargetColor } from 'src/modules/query-builder/utils';
import {
  INITIALIZE_QUERY_BUILDER,
  ADD_QUERY_ENTITY_REQUEST,
  UPDATE_QUERY_ENTITY_INFOS,
  REMOVE_QUERY_ENTITY_REQUEST,
  CHANGE_QUERY_GROUP_OPERATOR_REQUEST,
  UPDATE_QUERY,
  MOVE_ENTITY_REQUEST,
  GET_TIMEFRAMES_REQUEST,
  GET_TIMEFRAMES_SUCCESS,
  ADD_QUERY_ITEM_WITH_INFOS_REQUEST,
  CHANGE_BASE_QUERY_REQUEST,
  CHANGE_BASE_QUERY_SUCCESS,
  UPDATE_CONVERSATION_QUERY_REQUEST,
  ADD_CONVERSATION_ITEM_REQUEST,
  ADD_CONVERSATION_QUERY_REQUEST,
  MOVE_CONVERSATION_ITEM_REQUEST,
  ADD_NEW_TARGET,
  UPDATE_TARGETS,
  GET_QUERY_MEMBERSHIP_REQUEST,
  CLEAR_QUERY_REQUEST,
  CHANGE_AUDIENCE_TIMEFRAME_REQUEST,
  CHANGE_RESULT_TIMEFRAME_REQUEST,
  CHANGE_QUERY_TYPE_REQUEST,
  COPY_TARGET,
  REPLACE_TARGET_QUERY_REQUEST,
  MERGE_TARGET_QUERY_REQUEST,
  SELECT_QUERY_GROUP_REQUEST,
  ADD_ITEM_WITH_INFOS_IN_SELECTED_GROUP_REQUEST,
  ADD_CONVERSATION_ITEM_IN_SELECTED_GROUP_REQUEST,
  CHANGE_QUERY_GROUP_AT_LEAST_NUMBER_REQUEST,
  ADD_BLOCK_IN_SELECTED_GROUP_REQUEST,
  CHANGE_DATA_SOURCE_REQUEST,
} from '../constants/action-types';

import {
  findGroupAndAddEntity,
  findEntityAndUpdate,
  findAndRemoveEntity,
  findAndChangeGroupOperator,
  findAndMoveEntity,
  findConversationEntityAndUpdate,
  findEntity,
  mapQueryBlocksWithUniqueIdAndFormat,
  appendQueryWithAndOperator,
  normalizeRootQueryForStanleyMerge,
  preprocessStanleyTargetQueryForApply,
  findAndSelectGroup,
  findSelectedGroup,
  findAndChangeGroupAtLeastNumber,
  getQueryItemsIds,
  createUniqueInfoIds,
  mergeMetadataWithQuerySources,
  removeEmptyBlocksFromQuery,
  removeEmptyItemsFromQuery,
  createBlankSearchQuery,
  isPlaceholderQueryEntity,
  insertQueryItem,
} from '../operations/query';
import {
  addQueryEntityRequest,
  clearAllQueriesRequest,
  initializeQueryBuilder,
  setLoadingStatus,
} from '../actions';
import { isEmpty } from '../utils/misc';
import sagaFetch from './SagaFetch';
import * as engineApi from '../services/engineApi';

function* handleInitializeQueryBuilder(action) {
  const { targetId } = action;

  yield put(addQueryEntityRequest(targetId, 'block', 0, true));
}

function* handleAddNewTarget(action) {
  const targets = yield select((state) => state.queryBuilder.targets);
  const targetId = uniqid();
  const colors = [];
  Object.keys(targets).forEach((id) => {
    colors.push(targets[id].target_color);
  });

  const { initialTargetQuery } = action;
  const hasInitialQuery =
    initialTargetQuery != null && typeof initialTargetQuery === 'object';

  const emptyRootQuery = {
    type: 'block',
    id: 0,
    operator: 'OR',
    arguments_list: [],
  };

  const preparedInitial = hasInitialQuery
    ? preprocessStanleyTargetQueryForApply(initialTargetQuery)
    : null;
  const shouldApplyStanleyQuery = Boolean(preparedInitial);
  const target_query = shouldApplyStanleyQuery
    ? mapQueryBlocksWithUniqueIdAndFormat(preparedInitial)
    : emptyRootQuery;

  if (shouldApplyStanleyQuery) {
    yield put(setLoadingStatus(true));
  }
  try {
    let queryEntitiesInfos = {};
    if (shouldApplyStanleyQuery) {
      const dataSource = yield select((state) => state.queryBuilder.dataSource);
      queryEntitiesInfos = yield* fetchAndMergeMetadataInfos(
        target_query,
        {},
        dataSource
      );
    }

    const currentTargets = yield select((state) => state.queryBuilder.targets);
    const newTargets = {
      ...currentTargets,
      [targetId]: {
        query_type: 'interest',
        target_label: '',
        target_color: getTargetColor(colors),
        target_query,
        queryEntitiesInfos,
        conversationQueries: {},
        audience_timeframe: 'last_year',
        results_timeframe: 'last_year',
        base_query: { whole_population: true, query_block: null },
      },
    };

    yield put({
      type: UPDATE_TARGETS,
      targetId,
      targets: newTargets,
    });

    if (shouldApplyStanleyQuery) {
      yield put({
        type: UPDATE_QUERY,
        targetId,
        target_query,
        searchingSize: true,
      });
    } else {
      yield put(addQueryEntityRequest(targetId, 'group', 0, true));
    }
  } finally {
    if (shouldApplyStanleyQuery) {
      yield put(setLoadingStatus(false));
    }
  }
}

function* handleCopyTarget(action) {
  const { fromTarget, toTarget } = action;
  const targets = yield select((state) => state.queryBuilder.targets);
  const colors = [];
  const newTargetId = toTarget === '-1' ? uniqid() : toTarget;
  Object.keys(targets).forEach((id) => {
    colors.push(targets[id].target_color);
  });

  let updatedTargets = {
    ...targets,
  };

  if (toTarget === '-1') {
    updatedTargets = {
      ...updatedTargets,
      [newTargetId]: {
        target_query: mapQueryBlocksWithUniqueIdAndFormat(
          updatedTargets[fromTarget].target_query
        ),
        target_label: '',
        target_color: getTargetColor(colors),
        query_type: 'interest',
        queryEntitiesInfos: updatedTargets[fromTarget].queryEntitiesInfos,
        conversationQueries: {},
        audience_timeframe: 'last_year',
        results_timeframe: 'last_year',
        base_query: { whole_population: true, query_block: null },
      },
    };
  } else {
    let copyQueryEntitiesInfos = {
      ...(updatedTargets[toTarget].queryEntitiesInfos || {}),
    };
    Object.entries(updatedTargets[fromTarget].queryEntitiesInfos).forEach(
      ([key, value]) => {
        copyQueryEntitiesInfos = {
          ...copyQueryEntitiesInfos,
          [key]: value,
        };
      }
    );

    updatedTargets = {
      ...updatedTargets,
      [toTarget]: {
        ...updatedTargets[toTarget],
        queryEntitiesInfos: copyQueryEntitiesInfos,
        target_query: appendQueryWithAndOperator(
          updatedTargets[toTarget].target_query,
          updatedTargets[fromTarget].target_query
        ),
      },
    };
  }

  yield put({
    type: UPDATE_TARGETS,
    targetId: toTarget === '-1' ? newTargetId : toTarget,
    targets: updatedTargets,
  });

  yield put({
    type: UPDATE_QUERY,
    targetId: newTargetId,
    target_query: updatedTargets[newTargetId].target_query,
    searchingSize: true,
  });
}

function* handleAddQueryEntityRequest(action) {
  const { targetId, groupId, entityType, addOneMore } = action;
  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );
  const entityId = uniqid();

  const builderType = yield select(
    (state) => state.queryBuilder.queryBuilderType
  );
  const newEntity =
    entityType === 'item'
      ? {
          type: builderType === 'conversation' ? 'conversation' : 'item',
          id: builderType === 'conversation' ? entityId : `tmp_${entityId}`,
        }
      : {
          type: 'block',
          id: entityId,
          operator: 'OR',
          arguments_list: [],
          selected: true,
        };

  let updatedQuery = findGroupAndAddEntity(currentQuery, groupId, newEntity);

  const isItemNoGroup =
    currentQuery.arguments_list.length === 0 && entityType === 'item';

  if (entityType === 'group' || isItemNoGroup) {
    updatedQuery = findAndSelectGroup(updatedQuery, entityId, isItemNoGroup);
  }

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: updatedQuery,
    searchingSize: currentQuery && isEmpty(currentQuery.arguments_list),
  });

  if (entityType !== 'item' && addOneMore) {
    yield put(addQueryEntityRequest(targetId, 'item', entityId));
  }

  if (builderType === 'conversation') {
    yield put({
      type: ADD_CONVERSATION_QUERY_REQUEST,
      targetId,
      parentGroupId: groupId,
      entityContent: undefined,
      entityId,
    });
  }
}

function* handleUpdateQueryEntityInfos(action) {
  const {
    targetId,
    parentGroupId,
    entityInfos,
    entityIndex,
    addOneMore = true,
  } = action;

  // Demographics / taxonomy adds dispatch UPDATE_QUERY_ENTITY_INFOS only to merge
  // infos in the reducer. Without parentGroupId + entityIndex, findEntityAndUpdate
  // would no-op and a stale UPDATE_QUERY would overwrite a concurrent add.
  if (entityIndex === undefined) {
    return;
  }

  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );

  const updatedQuery = findEntityAndUpdate(
    currentQuery,
    parentGroupId,
    entityInfos,
    entityIndex
  );

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: updatedQuery,
    searchingSize: true,
  });

  if (addOneMore) {
    yield put(addQueryEntityRequest(targetId, 'item', parentGroupId));
  }
}

function* handleRemoveQueryEntityRequest(action) {
  const { targetId, parentGroupId, entityIndex } = action;
  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );

  const foundItem = findEntity(currentQuery, parentGroupId, entityIndex);

  const updatedQuery = findAndRemoveEntity(
    currentQuery,
    parentGroupId,
    entityIndex
  );

  const cleanedQuery = removeEmptyBlocksFromQuery(updatedQuery, false);
  const nextQuery = cleanedQuery ?? {
    type: 'block',
    id: 0,
    operator: 'OR',
    arguments_list: [],
  };
  const hasNonTemporaryId =
    foundItem &&
    typeof foundItem.id === 'string' &&
    !foundItem.id.includes('tmp_');

  if (isEmpty(nextQuery.arguments_list)) {
    if (isPlaceholderQueryEntity(foundItem)) return;

    const builderType = yield select(
      (state) => state.queryBuilder.queryBuilderType
    );

    yield put({
      type: UPDATE_QUERY,
      targetId,
      target_query: createBlankSearchQuery(builderType),
      searchingSize: true,
    });
    return;
  }

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: nextQuery,
    searchingSize: hasNonTemporaryId,
  });
}

function* handleChangeQueryGroupOperatorRequest(action) {
  const { targetId, groupId, operator } = action;
  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );

  const updatedQuery = findAndChangeGroupOperator(
    currentQuery,
    groupId,
    operator
  );

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: updatedQuery,
    searchingSize: operator !== 'AT_LEAST',
  });
}

function* handleChangeQueryGroupAtLeastNumberRequest(action) {
  const { targetId, groupId, atLeast } = action;
  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );

  const updatedQuery = findAndChangeGroupAtLeastNumber(
    currentQuery,
    groupId,
    atLeast
  );

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: updatedQuery,
    searchingSize: true,
  });
}

function* handleMoveEntityRequest(action) {
  const { targetId, parentIdFrom, entityIndex, itemId, parentIdTo } = action;
  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );

  const clearedQuery = removeEmptyItemsFromQuery(currentQuery);

  const updatedQuery = findAndMoveEntity(
    clearedQuery,
    parentIdFrom,
    entityIndex,
    itemId,
    parentIdTo
  );

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: removeEmptyBlocksFromQuery(updatedQuery),
    searchingSize: true,
  });
}

function* handleFetchTimeframesRequest() {
  const timeframesList = yield select((state) => state.timeframes.data);

  if (timeframesList.length > 0) {
    return yield put({
      type: GET_TIMEFRAMES_SUCCESS,
      timeframes: timeframesList,
    });
  }

  const { response, error } = yield call(engineApi.fetchTimeframes);

  if (!error) {
    return yield put({
      type: GET_TIMEFRAMES_SUCCESS,
      timeframes: response.data,
    });
  }
}

function* handleAddQueryItemWithInfos(action) {
  const { targetId, itemInfos, groupId } = action;
  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );
  const newEntity = {
    type: itemInfos.type,
    id: itemInfos.id,
    class: itemInfos.class || null,
    label: itemInfos.label,
  };
  if (itemInfos.source) {
    newEntity.source = itemInfos.source;
  }
  const updatedQuery = insertQueryItem(currentQuery, groupId, newEntity);

  yield put({
    type: UPDATE_QUERY_ENTITY_INFOS,
    targetId,
    entityInfos: itemInfos,
    addOneMore: false,
  });

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: updatedQuery,
    searchingSize: true,
  });
}

function* handleChangeBaseQueryRequest(action) {
  const { category, targetId } = action;

  const base_query =
    category.id === 'all'
      ? { whole_population: true }
      : {
          whole_population: false,
          query_block: {
            type: 'block',
            operator: 'OR',
            arguments_list: [category],
          },
        };

  yield put({ type: CHANGE_BASE_QUERY_SUCCESS, base_query, targetId });

  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId]
  );
  const currentQueryBuilderType = yield select(
    (state) => state.queryBuilder.queryBuilderType
  );
  if (currentQueryBuilderType === 'default') {
    yield put({
      type: GET_QUERY_MEMBERSHIP_REQUEST,
      targetId,
      query: currentQuery,
    });
  }
}

function* handleUpdateConversationQueryRequest(action) {
  const {
    targetId,
    parentGroupId,
    entityContent,
    entityIndex,
    addOneMore = true,
  } = action;

  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );

  const updatedQuery = findConversationEntityAndUpdate(
    currentQuery,
    parentGroupId,
    entityContent,
    entityIndex
  );

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: updatedQuery,
    searchingSize: true,
  });

  if (addOneMore) {
    yield put(addQueryEntityRequest(targetId, 'item', parentGroupId));
  }
}

function* handleAddConversationItem(action) {
  const { targetId, itemInfos, groupId } = action;
  const entityId = uniqid();
  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );
  const newEntity = {
    type: 'conversation',
    id: entityId,
    value: itemInfos.query,
  };

  const withoutPlaceholders = removeEmptyItemsFromQuery(currentQuery);
  const targetGroupId = groupId || findSelectedGroup(withoutPlaceholders);
  const updatedQuery = insertQueryItem(currentQuery, targetGroupId, newEntity);
  const parentGroupId =
    targetGroupId ||
    updatedQuery.arguments_list?.[updatedQuery.arguments_list.length - 1]?.id;

  yield put({
    type: UPDATE_CONVERSATION_QUERY_REQUEST,
    targetId,
    parentGroupId,
    entityContent: itemInfos.query,
    entityId,
    addOneMore: false,
  });

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: updatedQuery,
    searchingSize: true,
  });
}

function* handleMoveConversationItem(action) {
  const { targetId, parentIdFrom, entityIndex, itemId, parentIdTo } = action;
  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );

  const updatedQuery = findAndMoveEntity(
    currentQuery,
    parentIdFrom,
    entityIndex,
    itemId,
    parentIdTo
  );

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: removeEmptyBlocksFromQuery(updatedQuery),
    searchingSize: true,
  });
}

function* getCurrentQueryAndType(targetId) {
  const currentQuery = yield select(
    (state) => state.queryBuilder.targets?.[targetId]
  );
  const currentOriginalQuery = yield select(
    (state) => state.queryBuilder.originalTargets?.[targetId]
  );
  const currentQueryBuilderType = yield select(
    (state) => state.queryBuilder.queryBuilderType
  );
  return {
    currentQuery: currentOriginalQuery || currentQuery,
    currentQueryBuilderType,
  };
}

function* maybeRequestMembership(targetId, query, queryBuilderType) {
  if (queryBuilderType === 'default') {
    yield put({
      type: GET_QUERY_MEMBERSHIP_REQUEST,
      targetId,
      query,
    });
  }
}

export function* handleUpdateQuery(action) {
  const { targetId, searchingSize } = action;
  const { currentQuery, currentQueryBuilderType } =
    yield* getCurrentQueryAndType(targetId);

  if (currentQueryBuilderType === 'default' && searchingSize) {
    yield* maybeRequestMembership(
      targetId,
      currentQuery,
      currentQueryBuilderType
    );
  }
}

function* handleQueryChange(action) {
  const { targetId } = action;
  const { currentQuery, currentQueryBuilderType } =
    yield* getCurrentQueryAndType(targetId);
  yield* maybeRequestMembership(
    targetId,
    currentQuery,
    currentQueryBuilderType
  );
}

export const handleClearQuery = handleQueryChange;
export const handleChangeAudienceTimeframeRequest = handleQueryChange;
export const handleChangeResultTimeframeRequest = handleQueryChange;
export const handleChangeQueryTypeRequest = handleQueryChange;

function* handleSelectQueryGroupRequest(action) {
  const { targetId, groupId } = action;
  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );

  const updatedQuery = findAndSelectGroup(currentQuery, groupId);

  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: updatedQuery,
    searchingSize: false,
  });
}

function* fetchAndMergeMetadataInfos(updatedQuery, existingInfos, dataSource) {
  const metadataIds = getQueryItemsIds(updatedQuery);
  const encoded = encodeURIComponent(metadataIds.join(','));

  const { response: metadataResponse, error: metadataError } = yield sagaFetch(
    engineApi.fetchQueryItemsMetadata,
    encoded,
    dataSource
  );

  if (metadataError) return existingInfos;

  const newInfos = createUniqueInfoIds(
    mergeMetadataWithQuerySources(metadataResponse.data, updatedQuery)
  );

  return {
    ...existingInfos,
    ...newInfos,
  };
}

function* updateQueryForTarget(targetId, updatedQuery) {
  yield put({
    type: UPDATE_QUERY,
    targetId,
    target_query: updatedQuery,
    searchingSize: true,
  });
}

function* handleAddConversationItemInSelectedGroup(action) {
  const { targetId, itemInfos } = action;
  const entityId = uniqid();

  const currentQuery = yield select(
    (state) => state.queryBuilder.targets[targetId].target_query
  );

  const newEntity = {
    type: 'conversation',
    id: entityId,
    value: itemInfos.query,
  };

  const groupId = findSelectedGroup(currentQuery);
  const updatedQuery = insertQueryItem(currentQuery, groupId, newEntity);

  yield put({
    type: UPDATE_CONVERSATION_QUERY_REQUEST,
    targetId,
    parentGroupId: groupId,
    entityContent: itemInfos.query,
    entityId,
    addOneMore: false,
  });

  yield* updateQueryForTarget(targetId, updatedQuery);
}

function* handleAddItemWithInfosInSelectedGroup(action) {
  const { targetId, itemInfos } = action;

  const [targets, currentQuery, dataSource] = yield all([
    select((state) => state.queryBuilder.targets),
    select((state) => state.queryBuilder.targets[targetId].target_query),
    select((state) => state.queryBuilder.dataSource),
  ]);

  const newEntity = {
    type: itemInfos.type,
    id: itemInfos.id,
    class: itemInfos.class || null,
    label: itemInfos.label,
    metadata: itemInfos.metadata,
    ...(itemInfos.source && { source: itemInfos.source }),
  };

  const updatedQuery = insertQueryItem(
    currentQuery,
    findSelectedGroup(currentQuery),
    newEntity
  );

  let updatedTargets = {};
  if (itemInfos?.metadata?.target_query) {
    const updatedNewQuery = mapQueryBlocksWithUniqueIdAndFormat(
      itemInfos?.metadata?.target_query
    );

    const existingInfos = targets[targetId]?.queryEntitiesInfos || {};

    const mergedInfos = yield* fetchAndMergeMetadataInfos(
      updatedNewQuery,
      existingInfos,
      dataSource
    );

    updatedTargets = {
      ...targets,
      [targetId]: {
        ...targets[targetId],
        queryEntitiesInfos: mergedInfos,
        target_query: updatedNewQuery,
      },
    };
  }

  yield put({
    type: UPDATE_QUERY_ENTITY_INFOS,
    targetId,
    entityInfos: itemInfos,
    originalTargets: updatedTargets,
    addOneMore: false,
  });

  yield* updateQueryForTarget(targetId, updatedQuery);
}

function* handleAddBlockInSelectedGroup(action) {
  const { targetId, block } = action;

  const [dataSource, targets] = yield all([
    select((state) => state.queryBuilder.dataSource),
    select((state) => state.queryBuilder.targets),
  ]);

  const updatedQuery = mapQueryBlocksWithUniqueIdAndFormat(
    block.metadata.target_query
  );

  const existingInfos = targets[targetId]?.queryEntitiesInfos || {};
  const queryEntitiesInfos = yield* fetchAndMergeMetadataInfos(
    updatedQuery,
    existingInfos,
    dataSource
  );

  const updatedTargets = {
    ...targets,
    [targetId]: {
      ...targets[targetId],
      queryEntitiesInfos,
      target_query: updatedQuery,
    },
  };

  yield put({
    type: UPDATE_TARGETS,
    targetId,
    targets: updatedTargets,
  });

  yield* updateQueryForTarget(targetId, updatedQuery);
}

function* handleReplaceTargetQuery(action) {
  const { targetId, targetQuery: rawTargetQuery } = action;

  const [dataSource, targets] = yield all([
    select((state) => state.queryBuilder.dataSource),
    select((state) => state.queryBuilder.targets),
  ]);

  if (!targets[targetId] || rawTargetQuery == null) {
    return;
  }

  const preparedTree = preprocessStanleyTargetQueryForApply(rawTargetQuery);
  if (!preparedTree) {
    return;
  }

  yield put(setLoadingStatus(true));
  try {
    const updatedQuery = mapQueryBlocksWithUniqueIdAndFormat(preparedTree);
    const queryEntitiesInfos = yield* fetchAndMergeMetadataInfos(
      updatedQuery,
      {},
      dataSource
    );

    const freshTargets = yield select((state) => state.queryBuilder.targets);
    if (!freshTargets[targetId]) {
      return;
    }

    const updatedTargets = {
      ...freshTargets,
      [targetId]: {
        ...freshTargets[targetId],
        queryEntitiesInfos,
        target_query: updatedQuery,
      },
    };

    yield put({
      type: UPDATE_TARGETS,
      targetId,
      targets: updatedTargets,
    });

    yield* updateQueryForTarget(targetId, updatedQuery);
  } finally {
    yield put(setLoadingStatus(false));
  }
}

function* handleMergeTargetQuery(action) {
  const { targetId, sourceTargetQuery } = action;

  const [dataSource, targets] = yield all([
    select((state) => state.queryBuilder.dataSource),
    select((state) => state.queryBuilder.targets),
  ]);

  const target = targets[targetId];
  if (!target || sourceTargetQuery == null) {
    return;
  }

  const preparedSource =
    preprocessStanleyTargetQueryForApply(sourceTargetQuery);
  if (!preparedSource) {
    return;
  }

  yield put(setLoadingStatus(true));
  try {
    const normalizedDest = normalizeRootQueryForStanleyMerge(
      target.target_query
    );
    const mergedQuery = appendQueryWithAndOperator(
      normalizedDest,
      preparedSource
    );

    const freshTargets = yield select((state) => state.queryBuilder.targets);
    const latest = freshTargets[targetId];
    if (!latest) {
      return;
    }

    const existingInfos = latest.queryEntitiesInfos || {};
    const queryEntitiesInfos = yield* fetchAndMergeMetadataInfos(
      mergedQuery,
      existingInfos,
      dataSource
    );

    const freshTargetsAfterFetch = yield select(
      (state) => state.queryBuilder.targets
    );
    const latestAfterFetch = freshTargetsAfterFetch[targetId];
    if (!latestAfterFetch) {
      return;
    }

    const updatedTargets = {
      ...freshTargetsAfterFetch,
      [targetId]: {
        ...latestAfterFetch,
        queryEntitiesInfos,
        target_query: mergedQuery,
      },
    };

    yield put({
      type: UPDATE_TARGETS,
      targetId,
      targets: updatedTargets,
    });

    yield* updateQueryForTarget(targetId, mergedQuery);
  } finally {
    yield put(setLoadingStatus(false));
  }
}

function* handleChangeDataSourceRequest(action) {
  const { clearQuery } = action;

  if (clearQuery) {
    yield put(clearAllQueriesRequest());
    yield put(initializeQueryBuilder(uniqid()));
  }
}

export function* queryBuilderActionWatcher() {
  yield takeEvery(INITIALIZE_QUERY_BUILDER, handleInitializeQueryBuilder);
  yield takeEvery(ADD_NEW_TARGET, handleAddNewTarget);
  yield takeEvery(COPY_TARGET, handleCopyTarget);
  yield takeEvery(REPLACE_TARGET_QUERY_REQUEST, handleReplaceTargetQuery);
  yield takeEvery(MERGE_TARGET_QUERY_REQUEST, handleMergeTargetQuery);
  yield takeEvery(ADD_QUERY_ENTITY_REQUEST, handleAddQueryEntityRequest);
  yield takeEvery(UPDATE_QUERY_ENTITY_INFOS, handleUpdateQueryEntityInfos);
  yield takeEvery(REMOVE_QUERY_ENTITY_REQUEST, handleRemoveQueryEntityRequest);
  yield takeEvery(
    CHANGE_QUERY_GROUP_OPERATOR_REQUEST,
    handleChangeQueryGroupOperatorRequest
  );
  yield takeEvery(MOVE_ENTITY_REQUEST, handleMoveEntityRequest);
  yield takeEvery(GET_TIMEFRAMES_REQUEST, handleFetchTimeframesRequest);
  yield takeEvery(
    ADD_QUERY_ITEM_WITH_INFOS_REQUEST,
    handleAddQueryItemWithInfos
  );
  yield takeLatest(CHANGE_BASE_QUERY_REQUEST, handleChangeBaseQueryRequest);
  yield takeLatest(
    UPDATE_CONVERSATION_QUERY_REQUEST,
    handleUpdateConversationQueryRequest
  );
  yield takeEvery(ADD_CONVERSATION_ITEM_REQUEST, handleAddConversationItem);
  yield takeEvery(MOVE_CONVERSATION_ITEM_REQUEST, handleMoveConversationItem);
  yield takeEvery(UPDATE_QUERY, handleUpdateQuery);
  yield takeLatest(CLEAR_QUERY_REQUEST, handleClearQuery);
  yield takeLatest(
    CHANGE_AUDIENCE_TIMEFRAME_REQUEST,
    handleChangeAudienceTimeframeRequest
  );
  yield takeLatest(
    CHANGE_RESULT_TIMEFRAME_REQUEST,
    handleChangeResultTimeframeRequest
  );
  yield takeLatest(CHANGE_QUERY_TYPE_REQUEST, handleChangeQueryTypeRequest);
  yield takeEvery(SELECT_QUERY_GROUP_REQUEST, handleSelectQueryGroupRequest);
  yield takeEvery(
    ADD_ITEM_WITH_INFOS_IN_SELECTED_GROUP_REQUEST,
    handleAddItemWithInfosInSelectedGroup
  );
  yield takeEvery(
    ADD_CONVERSATION_ITEM_IN_SELECTED_GROUP_REQUEST,
    handleAddConversationItemInSelectedGroup
  );
  yield takeEvery(
    CHANGE_QUERY_GROUP_AT_LEAST_NUMBER_REQUEST,
    handleChangeQueryGroupAtLeastNumberRequest
  );
  yield takeEvery(
    ADD_BLOCK_IN_SELECTED_GROUP_REQUEST,
    handleAddBlockInSelectedGroup
  );
  yield takeLatest(CHANGE_DATA_SOURCE_REQUEST, handleChangeDataSourceRequest);
}
